import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Binary, Download, Sparkles, Clapperboard, Loader2, QrCode } from "lucide-react";
import QRCode from "qrcode";
import { mistApi } from "@/api/mist";
import { setDirectorAsset } from "@/lib/directorPrefill";

// ── Pure cipher utilities ────────────────────────────────────────────────
const textToHex = (str) =>
  Array.from(str || "")
    .map((c) => c.charCodeAt(0).toString(16).padStart(2, "0"))
    .join(" ");

const applyAtbashCipher = (str) =>
  (str || "")
    .toUpperCase()
    .split("")
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (code >= 65 && code <= 90) return String.fromCharCode(90 - (code - 65));
      return ch;
    })
    .join("");

const encodeROT13 = (str) =>
  (str || "").replace(/[A-Za-z]/g, (c) =>
    String.fromCharCode(c.charCodeAt(0) + (c.toUpperCase() <= "M" ? 13 : -13)));

const encodeBase64 = (str) => {
  try { return btoa(unescape(encodeURIComponent(str || ""))); }
  catch { return "Encoding Error"; }
};

const encodeBinary = (str) =>
  Array.from(str || "").map((c) => c.charCodeAt(0).toString(2).padStart(8, "0")).join(" ");

const MODES = [
  { key: "hex_atbash", label: "Hex / Atbash" },
  { key: "base64", label: "Base64 String" },
  { key: "rot13", label: "ROT13 Alpha Shift" },
  { key: "binary", label: "Pure Binary Stream" },
];
const MODE_LABEL = { hex_atbash: "HEX PAYLOAD", base64: "BASE64 PAYLOAD", rot13: "ROT13 PAYLOAD", binary: "BINARY STREAM" };

const W = 1080;
const H = 1920;
const GREEN = "#39FF7A";
const CARBON = "#060A07";

// Word-wrap monospace tokens to a max pixel width.
function wrapTokens(ctx, tokens, maxW) {
  const lines = [];
  let line = "";
  for (const t of tokens) {
    const trial = line ? `${line} ${t}` : t;
    if (ctx.measureText(trial).width > maxW && line) {
      lines.push(line);
      line = t;
    } else {
      line = trial;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export default function FounderCipherStudio({ onSendToDirector }) {
  const [text, setText] = useState("FOLLOW THE MIST");
  const [cipherMode, setCipherMode] = useState("hex_atbash");
  const [showCipherHint, setShowCipherHint] = useState(true);
  const [qrDestination, setQrDestination] = useState("https://misteck.com");
  const [qrImg, setQrImg] = useState(null);
  const [sending, setSending] = useState(false);
  const canvasRef = useRef(null);

  const atbash = applyAtbashCipher(text);
  // Active payload driven by the selected preset.
  const payload =
    cipherMode === "base64" ? encodeBase64(text) :
    cipherMode === "rot13" ? encodeROT13(text) :
    cipherMode === "binary" ? encodeBinary(text) :
    textToHex(atbash);
  const payloadLabel = MODE_LABEL[cipherMode] || "PAYLOAD";
  // For wrapping: split base64 into chunks so it flows into rows too.
  const payloadTokens =
    cipherMode === "base64"
      ? (payload.match(/.{1,4}/g) || [payload])
      : payload.split(" ");

  const draw = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;

    // Carbon mesh background
    ctx.fillStyle = CARBON;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(57,255,122,0.07)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    // Digital frame
    const M = 70;
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 5;
    ctx.strokeRect(M, M, W - M * 2, H - M * 2);
    ctx.strokeStyle = "rgba(57,255,122,0.35)";
    ctx.lineWidth = 2;
    ctx.strokeRect(M + 16, M + 16, W - (M + 16) * 2, H - (M + 16) * 2);
    // Corner ticks
    ctx.strokeStyle = GREEN; ctx.lineWidth = 6;
    const t = 46;
    [[M, M, 1, 1], [W - M, M, -1, 1], [M, H - M, 1, -1], [W - M, H - M, -1, -1]].forEach(([cx, cy, dx, dy]) => {
      ctx.beginPath(); ctx.moveTo(cx, cy + dy * t); ctx.lineTo(cx, cy); ctx.lineTo(cx + dx * t, cy); ctx.stroke();
    });

    ctx.textBaseline = "top";
    ctx.shadowColor = GREEN;
    ctx.shadowBlur = 18;

    // Header label
    ctx.fillStyle = GREEN;
    ctx.font = "700 34px 'Courier New', monospace";
    ctx.fillText("MISTECK // CIPHER TRANSMISSION", M + 40, M + 44);

    // Atbash headline (glitch offsets)
    ctx.font = "900 96px 'Courier New', monospace";
    const headLines = wrapTokens(ctx, (atbash || "—").split(" "), W - (M + 40) * 2);
    let y = 360;
    headLines.forEach((ln) => {
      ctx.fillStyle = "rgba(255,60,90,0.55)"; ctx.fillText(ln, M + 40 - 4, y + 3);
      ctx.fillStyle = "rgba(60,180,255,0.55)"; ctx.fillText(ln, M + 40 + 4, y - 3);
      ctx.fillStyle = GREEN; ctx.fillText(ln, M + 40, y);
      y += 120;
    });

    // Divider
    ctx.shadowBlur = 8;
    ctx.strokeStyle = "rgba(57,255,122,0.5)"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(M + 40, y + 20); ctx.lineTo(W - M - 40, y + 20); ctx.stroke();
    y += 70;

    // Active preset payload
    ctx.fillStyle = "rgba(57,255,122,0.55)";
    ctx.font = "700 30px 'Courier New', monospace";
    ctx.fillText(payloadLabel, M + 40, y);
    y += 54;
    ctx.fillStyle = GREEN;
    const payFont = cipherMode === "binary" ? 32 : 40;
    ctx.font = `700 ${payFont}px 'Courier New', monospace`;
    const payLines = wrapTokens(ctx, payloadTokens, W - (M + 40) * 2);
    const maxRows = cipherMode === "binary" ? 20 : 16;
    payLines.slice(0, maxRows).forEach((ln) => { ctx.fillText(ln, M + 40, y); y += payFont + 16; });

    // Footer prompt
    ctx.shadowBlur = 16;
    ctx.fillStyle = GREEN;
    ctx.font = "700 32px 'Courier New', monospace";
    ctx.fillText("> DECODE TO ENTER", M + 40, H - M - 90);
    ctx.shadowBlur = 0;

    // Optional subtle cipher-key hint (bottom-left, clear of the QR on the right).
    if (showCipherHint) {
      const HINTS = {
        rot13: "// ENCRYPTION PROTOCOL: ROT-13 ALPHABET ROTATION",
        base64: "// ENCRYPTION PROTOCOL: BASE64 ALPHABETIC CHUNK",
        binary: "// ENCRYPTION PROTOCOL: 8-BIT PURE BINARY STREAM",
        hex_atbash: "// ENCRYPTION PROTOCOL: HEXADECIMAL ATBASH MATRIX",
      };
      ctx.textAlign = "left";
      ctx.font = "700 24px 'Courier New', monospace";
      ctx.fillStyle = "rgba(57,255,122,0.45)";
      ctx.fillText(HINTS[cipherMode] || HINTS.hex_atbash, M + 40, H - M - 150);
    }

    // QR payload — bottom-right, drawn last (its dark card masks any text behind).
    if (qrImg) {
      const qs = 240, pad = 60;
      const qx = W - qs - pad, qy = H - qs - pad - 120;
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#0a0a0a";
      ctx.fillRect(qx - 14, qy - 14, qs + 28, qs + 28);
      ctx.strokeStyle = GREEN; ctx.lineWidth = 3;
      ctx.strokeRect(qx - 14, qy - 14, qs + 28, qs + 28);
      ctx.drawImage(qrImg, qx, qy, qs, qs);
      ctx.fillStyle = GREEN;
      ctx.font = "700 22px 'Courier New', monospace";
      ctx.fillText("SCAN TO ENTER", qx - 14, qy - 46);
    }
  };

  // Generate the QR entirely client-side (no network) whenever the URL changes.
  useEffect(() => {
    const url = (qrDestination || "").trim();
    if (!url) { setQrImg(null); return; }
    QRCode.toDataURL(url, { margin: 1, width: 240, color: { dark: "#39FF7A", light: "#0a0a0a" } })
      .then((dataUrl) => { const img = new Image(); img.onload = () => setQrImg(img); img.src = dataUrl; })
      .catch(() => setQrImg(null));
  }, [qrDestination]);

  useEffect(() => { draw(); /* redraw on text / preset / hint / QR change */ // eslint-disable-next-line
  }, [text, cipherMode, showCipherHint, qrImg]);

  const download = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    try {
      const url = cv.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `misteck_cipher_${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Cipher reel asset downloaded ✓");
    } catch {
      toast.error("Download failed — try again");
    }
  };

  // Instant hand-off: ingest the canvas PNG into the Vault, stash it for Mist
  // Director, and navigate — it lands as Frame 1 (image reel) ready for overlays.
  const sendToDirector = async () => {
    const cv = canvasRef.current;
    if (!cv || sending) return;
    setSending(true);
    try {
      draw(); // ensure the QR + latest text are baked before capture
      const blob = await new Promise((res) => cv.toBlob(res, "image/png"));
      if (!blob) throw new Error("Canvas export failed");
      const file = new File([blob], `cipher_${Date.now()}.png`, { type: "image/png" });
      const r = await mistApi.ingest(file, "image");
      setDirectorAsset({ asset_id: r.id, filename: r.filename, kind: "image" });
      toast.success("Sent to Mist Director ✨");
      onSendToDirector?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Hand-off failed — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden bg-charcoal" data-testid="cipher-studio">
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 grid place-items-center border-2 border-[#39FF7A] bg-black text-[#39FF7A] shadow-[3px_3px_0px_#000]"><Binary size={20} /></span>
          <div className="leading-tight">
            <p className="font-mono text-[9px] font-black uppercase tracking-[0.24em] text-[#39FF7A]">Founder Only</p>
            <h2 className="font-serif text-2xl text-white leading-none">Cipher Studio</h2>
          </div>
        </div>

        <label className="block">
          <span className="block font-mono text-[10px] font-black uppercase tracking-widest text-white/60 mb-1.5">Your secret message</span>
          <textarea
            data-testid="cipher-input"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 120))}
            rows={2}
            placeholder="Type a phrase to encode…"
            className="w-full border-2 border-black bg-[#0d0d0d] text-[#39FF7A] placeholder:text-white/30 px-3 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-[#39FF7A] resize-none"
          />
        </label>

        <label className="block">
          <span className="block font-mono text-[10px] font-black uppercase tracking-widest text-white/60 mb-1.5">Cipher Style Preset</span>
          <select
            data-testid="cipher-mode-select"
            value={cipherMode}
            onChange={(e) => setCipherMode(e.target.value)}
            className="w-full border-2 border-black bg-[#0d0d0d] text-[#39FF7A] px-3 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-[#39FF7A] cursor-pointer"
          >
            {MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </label>

        <button onClick={() => setShowCipherHint((v) => !v)} data-testid="cipher-hint-toggle"
          className="w-full flex items-center justify-between border-2 border-black bg-[#0d0d0d] px-3 py-2.5 active:translate-x-[1px] active:translate-y-[1px]">
          <span className="font-mono text-[10px] font-black uppercase tracking-widest text-white/70">Stamp Cipher Mode Hint</span>
          <span className={`w-9 h-5 rounded-full border-2 border-black flex items-center px-0.5 transition-colors ${showCipherHint ? "bg-[#39FF7A] justify-end" : "bg-neutral-700 justify-start"}`}>
            <span className="w-3.5 h-3.5 rounded-full bg-black" />
          </span>
        </button>

        <label className="block">
          <span className="block font-mono text-[10px] font-black uppercase tracking-widest text-white/60 mb-1.5 flex items-center gap-1"><QrCode size={12} className="text-[#39FF7A]" /> QR Destination URL</span>
          <input
            data-testid="cipher-qr-url"
            value={qrDestination}
            onChange={(e) => setQrDestination(e.target.value)}
            placeholder="https://misteck.com"
            className="w-full border-2 border-black bg-[#0d0d0d] text-[#39FF7A] placeholder:text-white/30 px-3 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-[#39FF7A]"
          />
        </label>

        {/* Live decoded readouts */}
        <div className="grid grid-cols-1 gap-2">
          <div className="border-2 border-white/15 bg-black/40 p-2.5" data-testid="cipher-atbash-out">
            <span className="font-mono text-[8px] font-black uppercase tracking-widest text-white/40">Atbash</span>
            <p className="font-mono text-[13px] text-[#39FF7A] break-words">{atbash || "—"}</p>
          </div>
          <div className="border-2 border-white/15 bg-black/40 p-2.5" data-testid="cipher-payload-out">
            <span className="font-mono text-[8px] font-black uppercase tracking-widest text-white/40">{payloadLabel}</span>
            <p className="font-mono text-[11px] text-[#39FF7A]/80 break-words">{payload || "—"}</p>
          </div>
        </div>

        {/* Canvas preview (rendered at 1080x1920, CSS-scaled) */}
        <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            data-testid="cipher-canvas"
            className="border-2 border-[#39FF7A]/50 shadow-[0_0_24px_rgba(57,255,122,0.25)]"
            style={{ width: "220px", height: "391px" }}
          />
        </div>
      </div>

      <div className="shrink-0 relative z-30 border-t-4 border-black bg-[#141414] px-4 py-3 space-y-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        {onSendToDirector && (
          <button
            onClick={sendToDirector}
            disabled={sending}
            data-testid="cipher-send-director"
            className="w-full flex items-center justify-center gap-2 border-2 border-[#39FF7A] bg-charcoal text-[#39FF7A] font-black font-mono text-sm uppercase tracking-widest px-4 py-3 shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Clapperboard size={16} />} {sending ? "Sending…" : "Send to Mist Director"}
          </button>
        )}
        <button
          onClick={download}
          data-testid="cipher-download"
          className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#39FF7A] text-black font-black font-mono text-sm uppercase tracking-widest px-4 py-3 shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
        >
          <Download size={16} /> Download Reel Asset (PNG)
        </button>
        <p className="text-center font-mono text-[8px] uppercase tracking-widest text-white/40 flex items-center justify-center gap-1">
          <Sparkles size={10} className="text-[#39FF7A]" /> 1080×1920 · drop into Mist Director
        </p>
      </div>
    </div>
  );
}
